/**
 * `interactions` procedure group — the timeline spine.
 *
 * One `interactions` row per (contact x source object), so the timeline is a single
 * indexed range scan rather than a six-way UNION. The full body lives in S3 and is
 * fetched through a separate presigned procedure; the spine exposes only `hasBody`
 * plus a render-safe `preview`, so `body_s3_key` never crosses the wire.
 */

import { z } from 'zod';
import {
  isoDateTimeSchema,
  jsonObjectSchema,
  nonBlankString,
  paginationInputSchema,
  uuidSchema,
} from './common.ts';
import { contactRefSchema } from './contacts.ts';
import {
  channelSchema,
  directionSchema,
  interactionKindSchema,
  interactionSourceTypeSchema,
  visibilitySchema,
} from './enums.ts';

/** `interactions.preview` is capped so a list render never pulls a whole email. */
export const MAX_INTERACTION_PREVIEW_LENGTH = 500;

export const MAX_NOTE_BODY_LENGTH = 20_000;

/**
 * One timeline row.
 *
 * As with `contactSchema`, the fields outside the share whitelist
 * (`INTERACTION_SCOPE_FIELDS` in `@crm/db`) are nullable, because a row reached
 * through a grant carries only `id`, `kind`, `occurredAt`, `title` at
 * `identity_notes`, plus `preview` and the body link at `full`. **Null means "not
 * visible at this scope"**, and every field is populated for an owned row.
 */
export const interactionSchema = z.object({
  id: uuidSchema,
  /** The OWNING org. For a shared row this is the grantor, not the caller's org. */
  orgId: uuidSchema,
  contactId: uuidSchema,
  userId: uuidSchema.nullable(),
  kind: interactionKindSchema,
  channel: channelSchema.nullable(),
  direction: directionSchema.nullable(),
  occurredAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  title: z.string().nullable(),
  preview: z.string().max(MAX_INTERACTION_PREVIEW_LENGTH).nullable(),
  /** True when a full body exists in S3; fetch it with `interactions.body`. */
  hasBody: z.boolean(),
  eventId: uuidSchema.nullable(),
  sourceType: interactionSourceTypeSchema.nullable(),
  sourceId: uuidSchema.nullable(),
  visibility: visibilitySchema.nullable(),
  sentiment: z.string().nullable(),
  isPinned: z.boolean().nullable(),
  /**
   * True when an LLM extraction produced this row. The UI must mark it and link to
   * the source span — model output is never presented as user-authored fact.
   *
   * `null` means UNKNOWN, not false: a shared row's provenance is outside every
   * share scope. Render an unknown-provenance row as unverified rather than as
   * user-authored — treating null as false is exactly the mistake this field
   * exists to prevent.
   */
  isAiGenerated: z.boolean().nullable(),
  metadata: jsonObjectSchema.nullable(),
  createdBy: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema.nullable(),
  updatedAt: isoDateTimeSchema.nullable(),
});

/** An org-wide feed row: the interaction plus the contact it hangs off. */
export const interactionFeedItemSchema = z.object({
  interaction: interactionSchema,
  contact: contactRefSchema,
});

// --- inputs ------------------------------------------------------------------

/** Cursor is on `(occurred_at, id)` descending. */
export const interactionTimelineInputSchema = paginationInputSchema.extend({
  contactId: uuidSchema,
  kind: z.array(interactionKindSchema).max(12).optional(),
  channel: z.array(channelSchema).max(7).optional(),
  /** Inclusive bounds on `occurredAt`. */
  since: isoDateTimeSchema.optional(),
  until: isoDateTimeSchema.optional(),
  pinnedFirst: z.boolean().default(false),
});

export const interactionFeedInputSchema = paginationInputSchema.extend({
  kind: z.array(interactionKindSchema).max(12).optional(),
  channel: z.array(channelSchema).max(7).optional(),
  contactId: z.array(uuidSchema).max(50).optional(),
  since: isoDateTimeSchema.optional(),
  until: isoDateTimeSchema.optional(),
});

/**
 * The manual "log an interaction" write. Replay-safe: the caller sends an
 * `Idempotency-Key` header and the server dedupes on `(org_id, dedupe_key)`, so an
 * offline outbox can flush the same note twice without creating two rows.
 */
export const interactionCreateNoteInputSchema = z.object({
  contactId: uuidSchema,
  body: nonBlankString(MAX_NOTE_BODY_LENGTH, 'body'),
  title: z.string().max(300).nullish(),
  /** Defaults to server `now`; set explicitly to backfill a past conversation. */
  occurredAt: isoDateTimeSchema.optional(),
  channel: channelSchema.nullish(),
  direction: directionSchema.nullish(),
  eventId: uuidSchema.nullish(),
  visibility: visibilitySchema.default('org'),
  isPinned: z.boolean().default(false),
});

// --- inferred types ----------------------------------------------------------

export type Interaction = z.output<typeof interactionSchema>;
export type InteractionFeedItem = z.output<typeof interactionFeedItemSchema>;
export type InteractionTimelineInput = z.input<typeof interactionTimelineInputSchema>;
export type InteractionCreateNoteInput = z.input<typeof interactionCreateNoteInputSchema>;
